<script setup lang="ts">
import { ref } from 'vue';
import { useUser } from '@/composables/useUser';
import { useMobile } from '@/composables/useMobile';
import { useNavSections } from '@/composables/useNavSections';
import { useOpenCoverageAlertsCount } from '@/composables/useOpenCoverageAlerts';
import AnnouncementsDrawer from './announcements-drawer.vue';

const { user, logout } = useUser();
const { isMobile } = useMobile();
const { sections } = useNavSections();
// Planning carries the open coverage alerts: visits about to start without a shift.
const openCoverageAlerts = useOpenCoverageAlertsCount();

function badgeFor(key: string): number {
  return key === 'planning' ? openCoverageAlerts.value : 0;
}

const mobileNavOpen = ref(false);
</script>
<template>
  <v-app-bar :elevation="2">
    <v-app-bar-nav-icon
      v-if="user && isMobile"
      aria-label="Abrir menú de navegación"
      @click="mobileNavOpen = !mobileNavOpen"
    />
    <a
      class="logo-link"
      href="/"
    >
      <v-img
        class="ml-2 ml-sm-6"
        src="@assets/images/logo.png"
        max-height="100"
        max-width="100"
        contain
      />
    </a>
    <v-list
      v-if="user && !isMobile"
      class="d-flex desktop-nav"
      color="transparent"
    >
      <v-list-item
        v-for="section in sections"
        :key="section.key"
        :title="section.title"
        :href="section.href"
        :active="section.active"
        class="rounded-pill"
        active-class="text-primary"
      >
        <!-- The count sits on the label like an exponent, so the pill stays as
             narrow as its text. -->
        <template #title="{ title }">
          <v-badge
            v-if="badgeFor(section.key)"
            :content="badgeFor(section.key)"
            class="nav-count"
            color="error"
            max="99"
            offset-x="-13"
            offset-y="2"
            label="Alertas de cobertura abiertas"
          >
            {{ title }}
          </v-badge>
          <template v-else>
            {{ title }}
          </template>
        </template>
      </v-list-item>
    </v-list>
    <v-spacer />
    <AnnouncementsDrawer v-if="user" />
    <v-menu v-if="user">
      <template #activator="{ props }">
        <v-btn
          icon="mdi-account-circle"
          size="large"
          color="primary"
          v-bind="props"
        />
      </template>
      <v-list>
        <v-list-item class="text-body-2">
          <v-icon icon="mdi-email-outline" />
          {{ user?.email }}
        </v-list-item>
        <v-list-item class="text-body-2">
          <v-icon icon="mdi-domain" />
          {{ user?.companyName }}
        </v-list-item>
        <v-list-item>
          <v-btn
            block
            @click="logout"
          >
            Cerrar Sesión
          </v-btn>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
  <Teleport to="body">
    <v-navigation-drawer
      v-if="user && isMobile"
      v-model="mobileNavOpen"
      location="left"
      temporary
      border="0"
    >
      <v-list>
        <v-list-item
          v-for="section in sections"
          :key="section.key"
          :title="section.title"
          :href="section.href"
          :active="section.active"
          active-class="text-primary"
        >
          <template
            v-if="badgeFor(section.key)"
            #append
          >
            <v-badge
              :content="badgeFor(section.key)"
              color="error"
              max="99"
              label="Alertas de cobertura abiertas"
              inline
            />
          </template>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </Teleport>
</template>
<style scoped>
/* Vuetify 3.12 sets a 40px min-width on v-list-item__content; short labels
   (e.g. "Hoy") then sit left-of-center in the pill. Let the content hug its
   label so the horizontal nav pills stay centered. */

.desktop-nav :deep(.v-list-item__content) {
  min-width: 0;
}

/* Vuetify clips the list item's content and title; the floating count sits just
   outside the label and would be cut off. Nav labels are one short line. */

.desktop-nav :deep(.v-list-item__content),
.desktop-nav :deep(.v-list-item-title) {
  overflow: visible;
}

/* The count starts 1px after the label (offset-x) instead of over its last
   letters; with the pill's 16px padding, the margin keeps one- and two-digit
   counts inside it ("99+" overhangs by 2px). */

.nav-count {
  margin-inline-end: 12px;
}

/* Shorter than Vuetify's 20px badge, so the count reads as an exponent; the text
   keeps the badge's caption size (0.75rem), the smallest the design system allows. */

.nav-count :deep(.v-badge__badge) {
  height: 16px;
  min-width: 16px;
  padding: 0 4px;
}

.logo-link {
  display: inline-block;
  width: 100px;
}

@media (min-width: 600px) {
  .logo-link {
    width: 150px;
  }
}
</style>
